import { useEffect, useRef, useState } from "react"

import { getOfflineCachePolicy } from "@/lib/offline/cachePolicies"
import type { OfflineCachePolicy } from "@/lib/offline/cachePolicies"
import { mediaCache } from "@/platform"

type CacheStorage = OfflineCachePolicy["storage"] | null

function getPolicyStorage(imageUrl?: string | null): CacheStorage {
  const policy = imageUrl ? getOfflineCachePolicy(imageUrl) : null
  return policy?.storage ?? null
}

function isAsyncCacheStorage(storage: CacheStorage) {
  return storage === "indexeddb" || storage === "filesystem-cache"
}

function getInitialCachedUrl(imageUrl?: string | null) {
  return isAsyncCacheStorage(getPolicyStorage(imageUrl))
    ? null
    : (imageUrl ?? null)
}

export function useCachedImageUrl(imageUrl?: string | null) {
  const objectUrlRef = useRef<string | null>(null)
  const [retryToken, setRetryToken] = useState(0)
  const [cacheState, setCacheState] = useState(() => ({
    imageUrl,
    retryToken: 0,
    storage: getPolicyStorage(imageUrl),
    cachedUrl: getInitialCachedUrl(imageUrl),
  }))

  if (
    cacheState.imageUrl !== imageUrl ||
    cacheState.retryToken !== retryToken
  ) {
    const storage = getPolicyStorage(imageUrl)
    setCacheState({
      imageUrl,
      retryToken,
      storage,
      cachedUrl:
        isAsyncCacheStorage(storage) &&
        cacheState.imageUrl === imageUrl &&
        storage === cacheState.storage &&
        cacheState.cachedUrl
          ? cacheState.cachedUrl
          : getInitialCachedUrl(imageUrl),
    })
  }

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }
  }, [])

  useEffect(() => {
    const policy = imageUrl ? getOfflineCachePolicy(imageUrl) : null

    if (!imageUrl) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
      return
    }

    if (
      policy?.storage !== "indexeddb" &&
      policy?.storage !== "filesystem-cache"
    ) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
      return
    }

    if (policy.storage === "filesystem-cache") {
      let isActive = true
      mediaCache
        .resolve(imageUrl, policy)
        .then((resolvedUrl) => {
          if (!isActive) return
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current)
            objectUrlRef.current = null
          }
          setCacheState((current) =>
            current.imageUrl === imageUrl
              ? {
                  imageUrl,
                  retryToken,
                  storage: "filesystem-cache",
                  cachedUrl: resolvedUrl,
                }
              : current,
          )
        })
        .catch((error) => {
          if (!isActive) return
          console.warn("[offline-cache] Failed to resolve cached media", error)
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current)
            objectUrlRef.current = null
          }
          setCacheState((current) =>
            current.imageUrl === imageUrl
              ? {
                  imageUrl,
                  retryToken,
                  storage: "filesystem-cache",
                  cachedUrl: imageUrl,
                }
              : current,
          )
        })
      return () => {
        isActive = false
      }
    }

    // Avoid starting a raw <img> load before the cached fetch creates a blob URL.
    const controller = new AbortController()
    let objectUrl: string | null = null
    let isActive = true

    fetch(imageUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok)
          throw new Error(`Image fetch failed: ${response.status}`)
        return response.blob()
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob)
        if (isActive) {
          setCacheState((current) =>
            current.imageUrl === imageUrl && objectUrl
              ? {
                  imageUrl,
                  retryToken,
                  storage: "indexeddb",
                  cachedUrl: objectUrl,
                }
              : current,
          )
          if (objectUrlRef.current && objectUrlRef.current !== objectUrl) {
            URL.revokeObjectURL(objectUrlRef.current)
          }
          objectUrlRef.current = objectUrl
        } else {
          URL.revokeObjectURL(objectUrl)
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          console.warn("[offline-cache] Failed to load cached image", error)
        }
      })

    return () => {
      isActive = false
      controller.abort()
      if (objectUrl && objectUrlRef.current !== objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [imageUrl, retryToken])

  const invalidate = async () => {
    if (!imageUrl || cacheState.storage !== "filesystem-cache") return

    const policy = getOfflineCachePolicy(imageUrl)
    if (policy?.storage !== "filesystem-cache") return

    await mediaCache.invalidate?.(imageUrl, policy)
    setRetryToken((current) => current + 1)
  }

  return {
    src: cacheState.cachedUrl,
    storage: cacheState.storage,
    invalidate,
  }
}
